import React, { useMemo } from 'react';
import {
  ICustomer,
  ICustomerContact,
  ISupplierContact,
  Maybe,
  ReportOutputType,
  ReportTemplateFormat,
} from '../../graphql/schema/p4types';
import { useFormContext } from 'react-hook-form';
import Dropdown from '../connectedForm/Dropdown';
import { IDocumentValues } from './documentCarouselUtil';
import BlurTextField from '../connectedForm/BlurTextField';
import MergeFieldContext from '../Context/MergeFieldContext';
import Label from '../connectedForm/Label';
import Wysiwyg from '../connectedForm/Wysiwyg';
import Legend from '../connectedForm/Legend';
import Fieldset from '../connectedForm/Fieldset';
import { IDropdownOptionRenderer } from '../../@types/entityTypes';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import { PREFERRED_CONTACT_STYLE } from '../report/ContactEmailEditor';
import { IEntityEmail } from '../../utils/EmailUtils';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';

const ContactOptionRenderer: IDropdownOptionRenderer = ({ option }: { option: IEntityEmail }) => {
  const { email, name, suffix } = option;

  const prefix = name && name !== email ? `${name} -` : '';

  return (
    <span style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span>
        {prefix}
        {email}
      </span>
      {suffix}
    </span>
  );
};

const useRecipientContactsList = (
  recipient: Maybe<ICustomer | ISupplier>,
  defaultEmailTo?: Array<IEntityEmail>,
  { additionalRecipients }: { additionalRecipients: Array<IEntityEmail> }
) => {
  const baseContactsList: Array<ICustomerContact | ISupplierContact> =
    recipient?.contacts?.filter((contact) => !!contact.email) || [];
  if (recipient?.email) {
    baseContactsList.push({ ...recipient, id: 'recipient' }); //Changes the id to 'recipient' to avoid conflicts with contact id's - Only the email address will eventually be used from the items in this list anyway  }
  }

  const recipientContactsList = baseContactsList.map((contact) => ({
    id: contact.id,
    email: contact.email,
    name: contact.name?.fullName,
    style: defaultEmailTo?.find((entry) => entry.email === contact.email) ? PREFERRED_CONTACT_STYLE : null,
  }));

  if (!!additionalRecipients) {
    recipientContactsList.unshift(...additionalRecipients);
  }

  return recipientContactsList;
};

const emailEntityOptionCreator = ({ label }: { label: string }) => {
  return {
    id: label,
    email: label,
    name: label,
    style: { borderRight: `7px solid ${p4Tokens.statusFlagBasic}` },
  };
};

/**
 *
 * @param entity - Used to obtain merge fields and default config (Note - Default config only has one usage).
 * @param recipient - Driving Customer or Supplier - Expected to contain a 'contacts' list.
 * @param singleRecipient - Indicates if all the documents being printed are for the same recipient (Customer or Supplier) - Some subfields are disabled
 *                          when this is false (Because each recipient will have different to/cc/subject lines), although this can be over-ridden via 'emailOptions'
 * @param emailOptions - Additional email configuration passed through from the printDocumentFoo call.
 */
const DocumentCarouselEmail = ({
  entity,
  recipient,
  singleRecipient,
  emailOptions,
}: {
  entity: EntityType;
  recipient: Maybe<ICustomer | ISupplier>;
  singleRecipient: boolean;
  emailOptions?: {
    additionalRecipients?: Array<IEntityEmail>;
    allowEmailTo?: boolean;
    allowEmailCc?: boolean;
    allowSubject?: boolean;
  };
}) => {
  const {
    watch,
    setValue,
    formState: { defaultValues },
  } = useFormContext<IDocumentValues>();

  const {
    additionalRecipients = [],
    allowEmailTo = singleRecipient,
    allowEmailCc = singleRecipient,
    allowSubject = singleRecipient,
  } = emailOptions ?? {};

  const contactsList = useRecipientContactsList(recipient, defaultValues?.email?.to, { additionalRecipients });

  const outputType = watch('outputType');
  const template = watch('template');
  const emailFrom = watch('email.from');
  const emailTo = watch('email.to');
  const emailCc = watch('email.cc');
  const emailSubject = watch('email.subject');
  const emailMessage = watch('email.message');

  const defaultMessage = useMemo(() => {
    return template?.templateContent ?? entity.configuration?.defaultConfig;
  }, [entity.configuration?.defaultConfig, template?.templateContent]);

  if (outputType !== ReportOutputType.Email && outputType !== ReportOutputType.EmailWithPreview) {
    return null;
  }

  const mergeFields =
    entity.mergeFields ||
    fieldMetaData.ReportType.enumValues.find((enumValue) => enumValue.value === template?.reportType)?.mergeFields ||
    [];

  return (
    <>
      <Legend label={'Email Configuration'} styles={{ root: { marginRight: 0 } }} />
      {emailFrom?.email ? (
        <BlurTextField data-testid={'email-from-readonly'} label="From" value={emailFrom.email} readOnly={true} />
      ) : null}
      {allowEmailTo ? (
        <Dropdown
          data-testid="email-to-input"
          dataid="emailTo"
          options={contactsList}
          label={'To'}
          multi={true}
          value={emailTo}
          onChange={(newEmails: Maybe<Array<IEntityEmail>>) => {
            setValue('email.to', newEmails ?? [], { shouldTouch: true, shouldDirty: true });
          }}
          creatable={true}
          rawLabel={true}
          labelKey={'name'}
          valueKey={'id'}
          valueRenderer={ContactOptionRenderer}
          optionRenderer={ContactOptionRenderer}
          newOptionCreator={emailEntityOptionCreator}
        />
      ) : null}
      {allowEmailCc ? (
        <Dropdown
          data-testid="email-cc-input"
          dataid="emailCc"
          options={contactsList}
          label={'Cc'}
          multi={true}
          value={emailCc}
          onChange={(newEmails: Maybe<Array<{ id: string } | { email: string }>>) => {
            setValue('email.cc', newEmails ?? [], { shouldTouch: true, shouldDirty: true });
          }}
          creatable={true}
          rawLabel={true}
          labelKey={'name'}
          valueKey={'id'}
          valueRenderer={ContactOptionRenderer}
          optionRenderer={ContactOptionRenderer}
          newOptionCreator={emailEntityOptionCreator}
        />
      ) : null}
      {allowSubject ? (
        <BlurTextField
          data-testid={'email-subject-input'}
          label="Subject"
          value={emailSubject}
          onChange={(newEmailSubject) => {
            setValue('email.subject', newEmailSubject, { shouldTouch: true, shouldDirty: true });
          }}
          enableOnChange
        />
      ) : null}
      <MergeFieldContext.Provider value={{ mergeFields }}>
        <div style={{ marginTop: '12px' }} />
        <Fieldset cols={1}>
          <Label>Message</Label>
          <Wysiwyg
            onChange={(value) => {
              setValue('email.message', value, { shouldTouch: true, shouldDirty: true });
            }}
            defaultValue={defaultMessage}
            value={emailMessage}
            allowImages={true}
            allowColorPicker={true}
          />
        </Fieldset>
      </MergeFieldContext.Provider>
    </>
  );
};

export default DocumentCarouselEmail;
